/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { CustomError } from "@Internal/Models";
import { Autobind } from "@Internal/Utils";

import {
    DesignCategory, DesignCategoryLocales, DesignCategoryRequest, DesignTypes
} from "App/Models/DesignCategory";

import { ProcessException } from "./Base";

type Result<T> = Promise<[ T | undefined, CustomError | undefined ]>;

/* Les categories des modeles. Le back relaie `console/design-category/*` sans le
   serialiser ( exemption `Internal/DesignCategories` de `Web/Hooks.ts` ). Une
   erreur de l'API revient en objet `{ Id, Message }`, `Id` = son code HTTP.

   ⚠️ UNE LANGUE PAR APPEL : l'API ecrit `name` dans la traduction de `locale`.
   Creer = le francais d'abord, puis une modification par autre langue remplie. */
export default class DesignCategoriesService {
    private service: Feathers.Service<any>;

    constructor( service: Feathers.Service<any> ) {
        this.service = service;
    }

    @Autobind
    async List( typeId: number ): Result<DesignCategory[]> {
        try {
            const query: { [ key: string ]: any } = {
                page: 1, limit: 500, sortBy: "order_id", orderBy: "ASC"
            };

            if( typeId > 0 )
                query.type = typeId;

            const result = await this.service.find( { query } );

            if( result === undefined || result._embedded === undefined )
                return [ undefined, this.error( result, "DesignCategories.List" ) ];

            const items: any[] = Array.isArray( result._embedded.items )
                ? result._embedded.items
                : [];

            return [ sort( items.map( deserialize ) ), undefined ];
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Create( request: DesignCategoryRequest ): Result<DesignCategory> {
        try {
            const result = await this.service.create(
                this.form( request, "fr", undefined )
            );

            if( result === undefined || result.id === undefined )
                return [ undefined, this.error( result, "DesignCategories.Create" ) ];

            return this.writeOtherLocales( Number( result.id ), request, deserialize( result ) );
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    /* Seules les langues CHANGEES partent ; le type et l'ordre avec la premiere. */
    @Autobind
    async Update(
        original: DesignCategory,
        request: DesignCategoryRequest
    ): Result<DesignCategory> {
        try {
            const changed = DesignCategoryLocales.filter( ( locale: string ): boolean =>
                ( request.Translations[ locale ] || "" ).trim() !== ""
                && ( request.Translations[ locale ] || "" ).trim()
                    !== ( original.Translations[ locale ] || "" ) );

            const sameShape = request.TypeId === original.TypeId
                && request.Order === original.Order;

            /* Rien de change dans les noms : un appel pour le type ou l'ordre seuls. */
            const locales = ( changed.length === 0 && !sameShape ) ? [ "" ] : changed;

            let latest: DesignCategory = original;

            for( const locale of locales ) {
                const result = await this.service.patch(
                    original.Id, this.form( request, locale, original )
                );

                if( result === undefined || result.id === undefined )
                    return [ undefined, this.error( result, "DesignCategories.Update" ) ];

                latest = deserialize( result );
            }

            return [ latest, undefined ];
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    /* L'ORDRE AU GLISSER ( Fred, 19/10/2026 ) : la liste d'un type, dans l'ordre voulu.
       Chaque categorie dont le rang change recoit `orderId` = sa place ( 1, 2, 3… ) ; le
       type part avec, l'API l'exige. Une a une : la premiere erreur arrete tout. */
    @Autobind
    async Reorder( ordered: DesignCategory[] ): Promise<CustomError | undefined> {
        try {
            for( let index = 0; index < ordered.length; index++ ) {
                const category = ordered[ index ];
                const order = index + 1;

                if( category.Order === order )
                    continue;

                const type = DesignTypes.find( ( item ): boolean => item.Id === category.TypeId );

                const result = await this.service.patch( category.Id, {
                    designType: ( type !== undefined ) ? type.ApiName : category.TypeName,
                    orderId: String( order )
                } );

                if( result === undefined || result.id === undefined )
                    return this.error( result, "DesignCategories.Reorder" );
            }

            return undefined;
        } catch( error ) {
            return ProcessException( error );
        }
    }

    @Autobind
    async Delete( id: number ): Promise<CustomError | undefined> {
        try {
            const result = await this.service.remove( id );

            if( result !== undefined && result.Id !== undefined && result.Message !== undefined )
                return this.error( result, "DesignCategories.Delete" );

            return undefined;
        } catch( error ) {
            return ProcessException( error );
        }
    }

    private async writeOtherLocales(
        id: number,
        request: DesignCategoryRequest,
        created: DesignCategory
    ): Result<DesignCategory> {
        let latest = created;

        for( const locale of DesignCategoryLocales ) {
            if( locale === "fr" || ( request.Translations[ locale ] || "" ).trim() === "" )
                continue;

            const result = await this.service.patch( id, this.form( request, locale, created ) );

            if( result === undefined || result.id === undefined )
                return [ undefined, this.error( result, "DesignCategories.Create" ) ];

            latest = deserialize( result );
        }

        return [ latest, undefined ];
    }

    /* `locale` vide : ni nom ni langue, le type et l'ordre seulement. */
    private form(
        request: DesignCategoryRequest,
        locale: string,
        original: DesignCategory | undefined
    ): { [ key: string ]: string } {
        const type = DesignTypes.find( ( item ): boolean => item.Id === request.TypeId );
        const form: { [ key: string ]: string } = {
            designType: ( type !== undefined ) ? type.ApiName : "",
            orderId: ( request.Order === null ) ? "" : String( request.Order )
        };

        if( locale !== "" ) {
            form.name = ( request.Translations[ locale ] || "" ).trim();
            form.locale = locale;
        } else if( original !== undefined ) {
            /* Sans nom, l'API garde le sien ; le francais reste celui d'avant. */
            form.locale = "fr";
            form.name = original.Translations.fr || original.Name;
        }

        return form;
    }

    private error( result: any, fallbackId: string ): CustomError {
        const id = ( result !== undefined && result.Id !== undefined ) ? result.Id : fallbackId;
        const message = ( result !== undefined && result.Message !== undefined )
            ? result.Message
            : "Unexpected design category response";

        return new CustomError( id, message );
    }
}

function deserialize( raw: any ): DesignCategory {
    const type = ( raw.design_type !== undefined && raw.design_type !== null )
        ? raw.design_type
        : {};
    const translations: { [ locale: string ]: string } = {};

    if( raw.translations !== undefined && raw.translations !== null )
        Object.keys( raw.translations ).forEach( ( locale: string ): void => {
            if( raw.translations[ locale ] !== null )
                translations[ locale ] = String( raw.translations[ locale ] );
        } );

    return {
        Id: Number( raw.id ),
        Name: String( translations.fr || raw.name || "" ),
        TypeId: Number( type.id ) || 0,
        TypeName: String( type.name || "" ),
        Translations: translations,
        Order: ( raw.order_id === null || raw.order_id === undefined )
            ? null
            : Number( raw.order_id )
    };
}

/* L'ordre choisi d'abord ( les categories sans ordre a la fin ), puis le nom. */
function sort( items: DesignCategory[] ): DesignCategory[] {
    return items.sort( ( a: DesignCategory, b: DesignCategory ): number => {
        if( a.Order !== b.Order ) {
            if( a.Order === null ) return 1;
            if( b.Order === null ) return -1;
            return a.Order - b.Order;
        }

        return a.Name.localeCompare( b.Name, "fr" );
    } );
}
